import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { Bell, ExternalLink } from "lucide-react";

import PageMeta from "../../../../shared/ui/common/PageMeta";
import Button from "../../../../shared/ui/button/Button";
import {
    useCancelPayment,
    useMarkPaymentPaid,
    usePayment,
    useSendPayment,
    useSendPaymentReminder,
    useUpdatePayment,
} from "../../application/usePayments";
import {
    PaymentLineItemInput,
    canCancel,
    canMarkPaid,
    canSend,
    canSendReminder,
    formatMoney,
    isEditable,
    reminderFailureFrom,
    sendFailureFrom,
    type SendFailure,
} from "../../domain/types";
import PaymentStatusBadge from "../components/PaymentStatusBadge";
import PaymentLineItemsEditor from "../components/PaymentLineItemsEditor";
import SendPaymentModal from "../components/SendPaymentModal";

const PaymentDetailPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const { data: payment, isLoading, isError } = usePayment(id);
    const updatePayment = useUpdatePayment();
    const sendPayment = useSendPayment();
    const markPaid = useMarkPaymentPaid();
    const cancelPayment = useCancelPayment();
    const sendReminder = useSendPaymentReminder();

    const [items, setItems] = useState<PaymentLineItemInput[]>([]);
    const [externalRef, setExternalRef] = useState("");
    const [showSendModal, setShowSendModal] = useState(false);
    const [sendFailure, setSendFailure] = useState<SendFailure | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!payment) return;
        setItems(
            payment.line_items.map((item) => ({
                description: item.description,
                quantity: item.quantity,
                unit_price: item.unit_price,
            }))
        );
        setExternalRef(payment.external_invoice_ref ?? "");
    }, [payment]);

    if (isLoading) {
        return <p className="text-sm text-gray-500">{t("common.loading")}</p>;
    }
    if (isError || !payment) {
        return (
            <p role="alert" className="text-sm text-red-600">
                {t("payments.errors.notFound")}
            </p>
        );
    }

    const editable = isEditable(payment);

    const handleSend = (input: {
        sent_at: string;
        request_payment_link: boolean;
    }) => {
        setError(null);
        setSendFailure(null);
        sendPayment.mutate(
            { id: payment.id, payload: input },
            {
                onSuccess: () => {
                    setSendFailure(null);
                    setShowSendModal(false);
                },
                // Reported inside the modal, which stays open. Setting it on
                // the page behind would put the explanation somewhere the
                // therapist cannot see it.
                onError: (failed) => setSendFailure(sendFailureFrom(failed)),
            }
        );
    };

    const concept = payment.line_items[0]?.description ?? "";

    return (
        <>
            <PageMeta
                title={`${t("payments.title")} — ${concept}`}
                description={t("payments.title")}
            />

            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="mb-1 flex items-center gap-3">
                        <h1 className="text-2xl font-semibold text-content-primary">
                            {payment.patient_name ?? concept}
                        </h1>
                        <PaymentStatusBadge status={payment.status} />
                    </div>
                    <p className="text-sm text-content-tertiary">
                        {concept}
                        {` · ${formatMoney(payment.total)}`}
                        {payment.sent_at ? ` · ${payment.sent_at}` : ""}
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    {canSend(payment) && (
                        <Button size="sm" onClick={() => setShowSendModal(true)}>
                            {t("payments.actions.send")}
                        </Button>
                    )}
                    {canMarkPaid(payment) && (
                        <Button
                            size="sm"
                            variant="outline"
                            loading={markPaid.isPending}
                            onClick={() => markPaid.mutate(payment.id)}
                        >
                            {t("payments.actions.markPaid")}
                        </Button>
                    )}
                    {canSendReminder(payment) && (
                        <Button
                            size="sm"
                            variant="outline"
                            startIcon={<Bell size={16} />}
                            loading={sendReminder.isPending}
                            onClick={() =>
                                sendReminder.mutate(payment.id, {
                                    // Names which of the two causes it was, so
                                    // the therapist knows whether to add an
                                    // address or ask an operator.
                                    onError: (failed) =>
                                        setError(
                                            t(
                                                `payments.reminderErrors.${reminderFailureFrom(failed)}`
                                            )
                                        ),
                                })
                            }
                        >
                            {t("payments.actions.sendReminder")}
                        </Button>
                    )}
                    {canCancel(payment) && (
                        <Button
                            size="sm"
                            variant="destructive"
                            loading={cancelPayment.isPending}
                            onClick={() =>
                                cancelPayment.mutate(payment.id, {
                                    onSuccess: () => navigate("/payments"),
                                })
                            }
                        >
                            {t("payments.actions.cancel")}
                        </Button>
                    )}
                </div>
            </div>

            {error && (
                <p role="alert" className="mb-4 text-sm text-red-600">
                    {error}
                </p>
            )}

            {payment.status === "pending" && (
                <p className="mb-4 text-sm text-content-tertiary">
                    {payment.reminder_count === 0
                        ? t("payments.reminders.noneSent")
                        : t("payments.reminders.sentCount", {
                              count: payment.reminder_count,
                              max: payment.max_reminders,
                          })}
                    {/* Which way the last one went. The patient's WhatsApp
                        consent chooses this, not the therapist. */}
                    {payment.last_reminder_channel && (
                        <>
                            {" "}
                            {t("payments.reminders.lastChannel", {
                                channel: t(
                                    `payments.reminders.channel.${payment.last_reminder_channel}`,
                                ),
                            })}
                        </>
                    )}
                </p>
            )}


            {payment.stripe_payment_link_url && (
                <a
                    href={payment.stripe_payment_link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:underline"
                >
                    <ExternalLink size={16} />
                    {t("payments.paymentLink")}
                </a>
            )}

            <dl className="mb-8 grid grid-cols-2 gap-4 rounded-xl border border-gray-200 p-5 text-sm sm:grid-cols-3 dark:border-gray-700">
                <div>
                    <dt className="text-content-tertiary">
                        {t("payments.fields.sentAt")}
                    </dt>
                    <dd className="font-medium text-content-primary">
                        {payment.sent_at ?? "—"}
                    </dd>
                </div>
                <div>
                    <dt className="text-content-tertiary">
                        {t("payments.fields.dueDate")}
                    </dt>
                    <dd className="font-medium text-content-primary">
                        {payment.due_date ?? "—"}
                    </dd>
                </div>
                <div>
                    <dt className="text-content-tertiary">
                        {t("payments.fields.total")}
                    </dt>
                    <dd className="font-medium text-content-primary">
                        {formatMoney(payment.total)}
                    </dd>
                </div>
                {/* Only when the payment was raised against one session. A
                    payment covering several, or none, has nothing to point at,
                    and an empty row would sit on most of them. */}
                {payment.session_id && (
                    <div>
                        <dt className="text-content-tertiary">
                            {t("payments.fields.session")}
                        </dt>
                        <dd>
                            <Link
                                to={`/session/${payment.session_id}`}
                                className="font-medium text-brand-600 hover:underline"
                            >
                                {t("payments.actions.viewSession")}
                            </Link>
                        </dd>
                    </div>
                )}
            </dl>

            <section className="mb-8 rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                <h2 className="mb-4 text-base font-semibold text-content-primary">
                    {t("payments.lineItems")}
                </h2>

                {editable ? (
                    <>
                        <PaymentLineItemsEditor items={items} onChange={setItems} />
                        <div className="mt-4 flex justify-end">
                            <Button
                                size="sm"
                                loading={updatePayment.isPending}
                                onClick={() =>
                                    updatePayment.mutate({
                                        id: payment.id,
                                        payload: { line_items: items },
                                    })
                                }
                            >
                                {t("common.save")}
                            </Button>
                        </div>
                    </>
                ) : (
                    <ReadOnlyLineItems payment={payment} />
                )}
            </section>

            <section className="mb-8 rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                <h2 className="mb-1 text-base font-semibold text-content-primary">
                    {t("payments.fields.externalInvoiceRef")}
                </h2>
                {/* Alchely issues no invoice. If the therapist raised one in
                    their own billing software, this records which. Editable in
                    any status: the invoice often follows the money. */}
                <p className="mb-3 text-sm text-content-tertiary">
                    {t("payments.externalInvoiceRefHelp")}
                </p>
                <div className="flex gap-2">
                    <input
                        id="external-invoice-ref"
                        aria-label={t("payments.fields.externalInvoiceRef")}
                        value={externalRef}
                        onChange={(e) => setExternalRef(e.target.value)}
                        className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                    />
                    <Button
                        size="sm"
                        variant="outline"
                        loading={updatePayment.isPending}
                        onClick={() =>
                            updatePayment.mutate({
                                id: payment.id,
                                payload: { external_invoice_ref: externalRef },
                            })
                        }
                    >
                        {t("common.save")}
                    </Button>
                </div>
            </section>

            {payment.reminders.length > 0 && (
                <section className="mb-8 rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                    <h2 className="mb-1 text-base font-semibold text-content-primary">
                        {t("payments.reminders.logTitle")}
                    </h2>
                    {/* The dates are the point: "we chased them" and "we chased
                        them on the 4th, the 8th and the 15th" are different
                        answers to a patient who says they heard nothing. */}
                    <p className="mb-4 text-sm text-content-tertiary">
                        {t("payments.reminders.sentCount", {
                            count: payment.reminder_count,
                            max: payment.max_reminders,
                        })}
                    </p>
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-gray-700">
                            <tr>
                                <th className="py-2">#</th>
                                <th className="py-2">{t("payments.fields.sentAt")}</th>
                                <th className="py-2">
                                    {t("payments.reminders.channelHeading")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {payment.reminders.map((reminder, index) => (
                                <tr
                                    key={`${reminder.sent_at}-${index}`}
                                    className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                >
                                    <td className="py-2 text-content-tertiary">
                                        {index + 1}
                                    </td>
                                    <td className="py-2 text-content-secondary">
                                        {new Date(reminder.sent_at).toLocaleString()}
                                    </td>
                                    <td className="py-2 text-content-secondary">
                                        {reminder.channel
                                            ? t(
                                                  `payments.reminders.channel.${reminder.channel}`
                                              )
                                            : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            {showSendModal && (
                <SendPaymentModal
                    isOpen={showSendModal}
                    payment={payment}
                    isSubmitting={sendPayment.isPending}
                    failure={sendFailure}
                    onClose={() => {
                        setSendFailure(null);
                        setShowSendModal(false);
                    }}
                    onConfirm={handleSend}
                />
            )}
        </>
    );
};

const ReadOnlyLineItems: React.FC<{
    payment: NonNullable<ReturnType<typeof usePayment>["data"]>;
}> = ({ payment }) => {
    const { t } = useTranslation();
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-gray-700">
                    <tr>
                        <th className="py-2">{t("payments.fields.description")}</th>
                        <th className="py-2 text-right">{t("payments.fields.quantity")}</th>
                        <th className="py-2 text-right">{t("payments.fields.unitPrice")}</th>
                        <th className="py-2 text-right">{t("payments.fields.subtotal")}</th>
                    </tr>
                </thead>
                <tbody>
                    {payment.line_items.map((item) => (
                        <tr key={item.id} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-2 text-content-secondary">
                                {item.description}
                            </td>
                            <td className="py-2 text-right">{item.quantity}</td>
                            <td className="py-2 text-right">{formatMoney(item.unit_price)}</td>
                            <td className="py-2 text-right">{formatMoney(item.subtotal)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <dl className="ml-auto mt-4 w-full space-y-1 text-sm sm:w-64">
                <div className="flex justify-between border-t border-gray-200 pt-1 text-base font-semibold text-gray-900 dark:border-gray-700 dark:text-white">
                    <dt>{t("payments.fields.total")}</dt>
                    <dd>{formatMoney(payment.total)}</dd>
                </div>
            </dl>
        </div>
    );
};

export default PaymentDetailPage;
